import React, { FC, ReactNode } from 'react';

import { styled } from '@mui/material';
import { alpha } from '@mui/material/styles';

import useTenantTranslation from '~/hooks/useTenantTranslation';

interface IProgressLine {
  value: number;
  progressString: ReactNode;
  completed?: boolean;
  noStatus?: boolean;
}

const ProgressLine: FC<IProgressLine> = ({
  value,
  progressString = '',
  completed = false,
  noStatus = false,
}) => {
  const { t } = useTenantTranslation();
  const safeValue = value > 100 ? 100 : value;

  return (
    <Wrapper>
      <Value data-testid="watchedPercentage">
        {!completed && <PercentValue>{safeValue}%</PercentValue>}
        {(!noStatus || completed) && (
          <Status>{t(completed ? 'common.completed' : 'common.viewed')}</Status>
        )}
      </Value>
      <Caption>{progressString}</Caption>
      <ProgressBar w={safeValue} />
    </Wrapper>
  );
};

export default ProgressLine;

const PercentValue = styled('span')`
  margin-right: 4px;
`;
const Wrapper = styled('div')`
  position: relative;
  width: 100%;
  height: 6px;
  background: ${({ theme }) => alpha(theme.palette.common.progressBar.color, 0.364706)};
  border-radius: 5px;
`;
const Value = styled('div')`
  position: absolute;
  left: 0;
  top: calc(100% + 7px);
  bottom: unset;
  color: ${({ theme }) => theme.palette.common.white};
  font-size: 13px;
  line-height: 1;
  z-index: 1;

  ${({ theme }) => theme.breakpoints.up('md')} {
    font-weight: bold;
    top: unset;
    bottom: calc(100% + 7px);
  }
`;
const Status = styled('span')`
  color: ${({ theme }) => theme.palette.common.gray};
  text-transform: uppercase;
  font-weight: normal;
  font-size: 12px;
`;
const Caption = styled('div')`
  position: absolute;
  right: 0;
  top: calc(100% + 4px);
  bottom: unset;

  ${({ theme }) => theme.breakpoints.up('md')} {
    top: unset;
    bottom: calc(100% + 4px);
  }
`;
const ProgressBar = styled('div', {
  shouldForwardProp: (prop) => prop !== 'w',
})<{ w: number }>`
  position: absolute;
  background: ${({ theme }) => theme.palette.common.progressBar.color};
  border-radius: 5px;
  height: 6px;
  left: 0;
  top: 0;
  width: ${({ w }) => w}%;
`;
